import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LogMiddlewareServiceAttributes } from '@albatrosdeveloper/ave-models-package';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CellRenderer,
  CellRendererParams,
} from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { IntegrationErrorsCompanyLookupService } from '../integration-errors-company-lookup.service';

/**
 * Celda "Interfaz": código en negrita + nombre en mayúsculas debajo, igual que
 * la columna equivalente de la pantalla de Latencia.
 */
@Component({
  selector: 'app-integration-error-interface-cell',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="text-sm leading-tight">
      <b>{{ params.data?.code }}</b>
      <br />
      <span class="uppercase">{{ params.data?.name }}</span>
    </div>
  `,
})
export class IntegrationErrorInterfaceCellComponent implements CellRenderer<LogMiddlewareServiceAttributes> {
  @Input() params!: CellRendererParams<LogMiddlewareServiceAttributes>;
}

/**
 * Celda "Tipo": el texto original del log más iconografía por dirección —
 * ERP (base de datos) y la app CSMOBILE (teléfono), con flecha según el
 * sentido de la integración. Tipos no reconocidos muestran solo el texto.
 */
@Component({
  selector: 'app-integration-error-type-cell',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="flex items-center gap-1.5 text-sm" [matTooltip]="typeText">
      @if (sides) {
        <mat-icon class="icon-size-4 text-gray-500" [svgIcon]="iconFor(sides.from)"></mat-icon>
        <mat-icon class="icon-size-3 text-gray-400" svgIcon="heroicons_outline:arrow-long-right"></mat-icon>
        <mat-icon class="icon-size-4 text-gray-500" [svgIcon]="iconFor(sides.to)"></mat-icon>
      }
      <span class="whitespace-nowrap">{{ typeText }}</span>
    </div>
  `,
})
export class IntegrationErrorTypeCellComponent implements CellRenderer<LogMiddlewareServiceAttributes> {
  @Input() params!: CellRendererParams<LogMiddlewareServiceAttributes>;

  get typeText(): string {
    return this.params.data?.type ?? '—';
  }

  get sides(): { from: string; to: string } | null {
    const parts = (this.params.data?.type ?? '').split('->');
    if (parts.length !== 2) return null;
    return { from: parts[0].trim().toUpperCase(), to: parts[1].trim().toUpperCase() };
  }

  iconFor(side: string): string {
    if (side.includes('ERP') || side.includes('SAP')) {
      return 'heroicons_outline:circle-stack';
    }
    if (side.includes('CSM') || side.includes('APP') || side.includes('MOBILE')) {
      return 'heroicons_outline:device-phone-mobile';
    }
    return 'heroicons_outline:cube';
  }
}

/**
 * Celda "Compañía": usa la compañía embebida en el log; si el registro solo
 * trae el id, la resuelve contra el catálogo (cacheado). Sin compañía se
 * muestra raya — en registros vacío no significa "Todas".
 */
@Component({
  selector: 'app-integration-error-company-cell',
  standalone: true,
  imports: [CommonModule],
  template: `<span class="text-sm">{{ companyName }}</span>`,
})
export class IntegrationErrorCompanyCellComponent implements CellRenderer<LogMiddlewareServiceAttributes> {
  @Input() params!: CellRendererParams<LogMiddlewareServiceAttributes>;

  private readonly lookup = inject(IntegrationErrorsCompanyLookupService);

  get companyName(): string {
    const company = this.params.data?.company;
    if (!company) return '—';
    return (
      company.name ??
      this.lookup.nameById(company._id ?? company.id) ??
      '—'
    );
  }
}

/**
 * Celda "Resultado": botón con burbuja de chat (mismo estilo que Latencia) que
 * abre el modal de respuesta con procesados/insertados/actualizados/errores.
 * Deshabilitado cuando el log no trae resultado.
 */
@Component({
  selector: 'app-integration-error-result-cell',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  template: `
    <button
      mat-button
      color="primary"
      [disabled]="!params.data?.result"
      (click)="params.data?.result && open()"
    >
      <mat-icon
        [svgIcon]="
          params.data?.result
            ? 'heroicons_solid:chat-bubble-left-ellipsis'
            : 'heroicons_outline:chat-bubble-left-ellipsis'
        "
      ></mat-icon>
    </button>
  `,
})
export class IntegrationErrorResultCellComponent implements CellRenderer<LogMiddlewareServiceAttributes> {
  @Input() params!: CellRendererParams<LogMiddlewareServiceAttributes>;

  open(): void {
    this.params.colDef?.cellRendererParams?.onClick?.(this.params.data);
  }
}

/**
 * Celda "Acción": menú con "Visualizar logs" — el detalle se abre desde acá,
 * no con clic en la fila, igual que en la pantalla de Latencia.
 */
@Component({
  selector: 'app-integration-error-actions-cell',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  template: `
    <button mat-icon-button [matMenuTriggerFor]="actionMenu" matTooltip="Acciones">
      <mat-icon>more_vert</mat-icon>
    </button>
    <mat-menu #actionMenu="matMenu">
      <button mat-menu-item (click)="viewLogs()">
        <mat-icon>visibility</mat-icon>
        <span>Visualizar logs</span>
      </button>
    </mat-menu>
  `,
})
export class IntegrationErrorActionsCellComponent implements CellRenderer<LogMiddlewareServiceAttributes> {
  @Input() params!: CellRendererParams<LogMiddlewareServiceAttributes>;

  viewLogs(): void {
    this.params.colDef?.cellRendererParams?.onClick?.(this.params.data);
  }
}
